import {
  FunctionField,
  SimpleShowLayout,
  useGetOne,
  usePermissions,
  useRecordContext,
} from "react-admin";
import { Box, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useShowTotals } from "../BookingShowContext";
import { HallBookingsBookingAgreedTotalEdit } from "./HallBookingsBookingAgreedTotalEdit";
import { HallBookingsBookingPhoneEdit } from "./HallBookingsBookingPhoneEdit";
import { resolveBookingDisplayMohalla } from "@/containers/bookings/resolve-booking-display-mohalla";
import {
  resolveBookingOrganiserSummaryFields,
  resolveBookingSadaratSummaryField,
} from "@/utils/resolve-booking-identity-display";
import { formatDisplayDateTime, formatListDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";

type AmountRow = { label: string; value: string | number; type?: string };

export const HallBookingsBookingSummary = () => {
  const record = useRecordContext();
  const { permissions } = usePermissions();
  const {
    depositAmount,
    rentAmount,
    thaalCount,
    thaalAmount,
    refundAmount,
    kitchenCleaningAmount,
    totalAmountPending,
  } = useShowTotals();
  const linkedRazaRequestId =
    record?.razaRequestId != null && String(record.razaRequestId).trim() !== ""
      ? String(record.razaRequestId)
      : "";
  const { data: linkedRazaRequest, isLoading: linkedRazaLoading } = useGetOne(
    "razaRequests",
    { id: linkedRazaRequestId },
    { enabled: Boolean(linkedRazaRequestId) }
  );
  if (!record) return null;
  const linkedRazaStatusText = !linkedRazaRequestId
    ? "No link"
    : linkedRazaLoading
      ? "Loading..."
      : linkedRazaRequest?.razaGranted
        ? "Granted"
        : "Pending";

  const extraExpenses = Number(record.extraExpenses) || 0;
  const agreedRaw = record.agreedTotalPayable;
  const agreedTotal =
    agreedRaw !== null && agreedRaw !== undefined && agreedRaw !== "" ? Number(agreedRaw) : null;
  const calculatedTotal = rentAmount + kitchenCleaningAmount + thaalAmount;
  const agreedTotalDiff =
    agreedTotal != null && Number.isFinite(agreedTotal) ? calculatedTotal - agreedTotal : 0;

  const amountsLeft: AmountRow[] = [
    { label: "Deposit", value: depositAmount },
    { label: "Contribution", value: rentAmount },
    ...(kitchenCleaningAmount > 0
      ? [{ label: "Kitchen Cleaning", value: kitchenCleaningAmount }]
      : []),
    ...(thaalAmount > 0
      ? [
          {
            label: `Thaal (${thaalCount})`,
            value: thaalAmount || 0,
          },
        ]
      : []),
    ...(extraExpenses > 0 ? [{ label: "Extra expenses", value: extraExpenses }] : []),
    ...(agreedTotal == null && extraExpenses < 0
      ? [{ label: "Discount / reduced rate (legacy)", value: Math.abs(extraExpenses) }]
      : []),
    ...((record.writeOffAmount as number) > 0
      ? [{ label: "Write Off", value: record.writeOffAmount as number }]
      : []),
    { label: "Calculated Hall Total", value: calculatedTotal },
    ...(agreedTotal != null && Number.isFinite(agreedTotal)
      ? [{ label: "Agreed Contribution", value: agreedTotal }]
      : []),
    ...(agreedTotalDiff > 0 ? [{ label: "Total Discount", value: agreedTotalDiff }] : []),
    { label: "Total Payable", value: totalAmountPending },
  ];

  const amountsRight: AmountRow[] = [
    { label: "Deposit Paid", value: record.depositPaidAmount as number },
    { label: "Paid", value: record.paidAmount as number },
    { label: "Jamaat Lagat Paid", value: (record.jamaatLagat as number) || 0 },
    { label: "Refund", value: refundAmount },
    ...((record.refundReturnAmount as number) > 0
      ? [
          { label: "Refund Returned", value: record.refundReturnAmount as number },
          {
            label: "Refund Returned On",
            value: formatListDate(record.refundReturnedOn as string),
            type: "date",
          },
        ]
      : []),
  ];

  const LabelValue = ({
    label,
    value,
    isMuted = false,
  }: {
    label: string;
    value: string | number;
    isMuted?: boolean;
  }) => (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, py: 0.75 }}>
      <Typography variant="body2" color={isMuted ? "text.secondary" : "text.primary"}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        color={isMuted ? "text.secondary" : "text.primary"}
      >
        {value}
      </Typography>
    </Box>
  );

  const identitySource = {
    organiser: record.organiser as string | null | undefined,
    itsNo: record.itsNo as string | null | undefined,
    requesterName: record.requesterName as string | null | undefined,
    requesterItsNo: record.requesterItsNo as string | null | undefined,
    sadarat: record.sadarat as string | null | undefined,
  };
  const summaryFieldsBeforePhone = resolveBookingOrganiserSummaryFields(identitySource);
  const summaryFieldsAfterPhone = [
    {
      label: "Mohalla",
      value:
        resolveBookingDisplayMohalla({
          displayMohalla: record.displayMohalla as string | null | undefined,
          mohalla: record.mohalla as string | null | undefined,
        }) ?? "—",
    },
    resolveBookingSadaratSummaryField(identitySource),
    { label: "PAN Card", value: (record.pancard as string) || "-" },
    { label: "Raza Status", value: linkedRazaStatusText },
    {
      label: "Booked On",
      value: formatDisplayDateTime(record.createdAt as string),
    },
  ];

  return (
    <Grid container spacing={1.5} sx={{ mb: 0 }}>
      <Grid sx={{ borderRight: 1, borderRightColor: "divider", pr: 1 }} size={{ xs: 12, md: 6 }}>
        <Typography variant="h6" gutterBottom>
          Summary
        </Typography>
        <SimpleShowLayout
          sx={{
            mt: 0.5,
            borderTop: 1,
            borderTopColor: "divider",
          }}
        >
          {summaryFieldsBeforePhone.map(({ label, value }) => (
            <FunctionField key={label} label={label} render={() => value} />
          ))}
          <FunctionField
            label="Phone"
            render={() => (
              <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                {(record.phone as string) || "—"}
                <HallBookingsBookingPhoneEdit />
              </Box>
            )}
          />
          {summaryFieldsAfterPhone.map(({ label, value }) => (
            <FunctionField key={label} label={label} render={() => value} />
          ))}
        </SimpleShowLayout>
      </Grid>
      {hasPermission(permissions, "bookings.edit") && (
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6" gutterBottom>
            Payments
          </Typography>

          <Grid container spacing={1} sx={{ borderTop: 1, borderTopColor: "divider", pt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {amountsLeft.map((item, index) => (
                <Box key={item.label}>
                  <LabelValue label={item.label} value={`₹${item.value ?? "0.00"}`} />
                  {index !== amountsLeft.length - 1 && <Divider />}
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {amountsRight.map((item, index) => (
                <Box key={item.label}>
                  <LabelValue
                    label={item.label}
                    value={`${!item.type ? "₹" : ""}${item.value ?? "0.00"}`}
                    isMuted={item.type === "date"}
                  />
                  {index !== amountsRight.length - 1 && <Divider />}
                </Box>
              ))}
            </Grid>
          </Grid>
          <HallBookingsBookingAgreedTotalEdit embedded />
        </Grid>
      )}
    </Grid>
  );
};

export default HallBookingsBookingSummary;
